import { z } from 'zod';
import {
  ApplicationSource,
  ApplicationStatus,
  InterviewMode,
  InterviewResult,
  InterviewStatus,
} from '../enums';
import { MAX_BULK_SIZE } from '../constants';
import { booleanish, dateSchema, paginationSchema, zEnum } from './common';

export const applyToJobSchema = z.object({
  jobId: z.string().uuid(),
  coverNote: z.string().trim().max(1000).optional().or(z.literal('')),
  /** Overrides the profile's default résumé for this one application. */
  resumeDocumentId: z.string().uuid().optional(),
});
export type ApplyToJobInput = z.infer<typeof applyToJobSchema>;

/** Admin/staff pushing sourced candidates into a job's pipeline. */
export const assignCandidatesSchema = z.object({
  jobId: z.string().uuid(),
  seekerIds: z.array(z.string().uuid()).min(1).max(MAX_BULK_SIZE),
  source: zEnum(ApplicationSource).default(ApplicationSource.ADMIN_ASSIGNED),
  remarks: z.string().trim().max(500).optional(),
});
export type AssignCandidatesInput = z.infer<typeof assignCandidatesSchema>;

export const updateApplicationStatusSchema = z.object({
  status: zEnum(ApplicationStatus),
  remarks: z.string().trim().max(1000).optional(),
  /** Shown to the candidate when rejecting; internal remarks stay hidden. */
  reasonCode: z.string().trim().max(80).optional(),
  notifyCandidate: z.boolean().default(true),
});
export type UpdateApplicationStatusInput = z.infer<typeof updateApplicationStatusSchema>;

export const bulkUpdateStatusSchema = z.object({
  applicationIds: z.array(z.string().uuid()).min(1).max(MAX_BULK_SIZE),
  status: zEnum(ApplicationStatus),
  remarks: z.string().trim().max(1000).optional(),
  notifyCandidates: z.boolean().default(true),
});
export type BulkUpdateStatusInput = z.infer<typeof bulkUpdateStatusSchema>;

export const applicationSearchSchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  jobId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  seekerId: z.string().uuid().optional(),
  status: z
    .union([
      z.array(zEnum(ApplicationStatus)),
      zEnum(ApplicationStatus).transform((v) => [v]),
    ])
    .optional(),
  source: zEnum(ApplicationSource).optional(),
  appliedFrom: dateSchema.optional(),
  appliedTo: dateSchema.optional(),
  /** Only candidates whose documents have all been verified. */
  documentsVerified: booleanish.optional(),
});
export type ApplicationSearchQuery = z.infer<typeof applicationSearchSchema>;

// --- Interviews ------------------------------------------------------------

export const scheduleInterviewSchema = z.object({
  applicationId: z.string().uuid(),
  scheduledAt: dateSchema,
  durationMinutes: z.coerce.number().int().min(5).max(480).default(30),
  mode: zEnum(InterviewMode).default(InterviewMode.IN_PERSON),
  venue: z.string().trim().max(300).optional().or(z.literal('')),
  meetingLink: z.string().trim().url().max(500).optional().or(z.literal('')),
  interviewerName: z.string().trim().max(120).optional().or(z.literal('')),
  interviewerPhone: z.string().trim().max(20).optional().or(z.literal('')),
  round: z.coerce.number().int().min(1).max(10).default(1),
  instructions: z.string().trim().max(1000).optional().or(z.literal('')),
  notifyCandidate: z.boolean().default(true),
});
export type ScheduleInterviewInput = z.infer<typeof scheduleInterviewSchema>;

/** Walk-in / bulk drive: one slot, many candidates. */
export const createInterviewBatchSchema = z.object({
  name: z.string().trim().min(3).max(150),
  jobId: z.string().uuid(),
  scheduledAt: dateSchema,
  endsAt: dateSchema.optional(),
  mode: zEnum(InterviewMode).default(InterviewMode.WALK_IN),
  venue: z.string().trim().max(300).optional().or(z.literal('')),
  capacity: z.coerce.number().int().min(1).max(5000).optional(),
  interviewerName: z.string().trim().max(120).optional().or(z.literal('')),
  instructions: z.string().trim().max(2000).optional().or(z.literal('')),
});
export type CreateInterviewBatchInput = z.infer<typeof createInterviewBatchSchema>;

export const addToBatchSchema = z.object({
  applicationIds: z.array(z.string().uuid()).min(1).max(MAX_BULK_SIZE),
  notifyCandidates: z.boolean().default(true),
});
export type AddToBatchInput = z.infer<typeof addToBatchSchema>;

export const rescheduleInterviewSchema = z.object({
  scheduledAt: dateSchema,
  reason: z.string().trim().max(500).optional(),
  notifyCandidate: z.boolean().default(true),
});

export const interviewFeedbackSchema = z.object({
  result: zEnum(InterviewResult),
  status: zEnum(InterviewStatus).default(InterviewStatus.COMPLETED),
  rating: z.coerce.number().int().min(1).max(5).optional(),
  technicalScore: z.coerce.number().int().min(0).max(100).optional(),
  communicationScore: z.coerce.number().int().min(0).max(100).optional(),
  feedback: z.string().trim().max(2000).optional().or(z.literal('')),
  /** Salary agreed in the interview — carried into the offer. */
  offeredSalary: z.coerce.number().min(0).max(10_000_000).optional(),
  /** Advance the application automatically to SELECTED / REJECTED. */
  applyToApplication: z.boolean().default(true),
});
export type InterviewFeedbackInput = z.infer<typeof interviewFeedbackSchema>;

export const savedJobSchema = z.object({ jobId: z.string().uuid() });
